<template>
	<div>
		<b-img src="/images/importer/module-equipment/equipment.jpg"></b-img>
		<p>
			The Equipment module is for setting up equipment slots on your character, to support equippable items. Currently, only Feral characters have support for equipment, with the Saddle and Belly Rig.
		</p>
        
		<p>
			<span class="font-weight-bold">Generate Equipment Slots:</span> This button will generate equipment slots for the various body parts on your character, and add them to the Equipment Slots list.
			There is no need to manually create or modify this Equipment Slots list, but it can be used to easily find the EquipmentSlot objects in your character's hierarchy by clicking on an item.
		</p>
        
		<b-img src="/images/importer/module-equipment/equipment-slot.jpg"></b-img>
		<p>
			The Equipment Slot component on each slot object has some child objects which you will want to configure.
		</p>
        
		<p>
			<span class="font-weight-bold">Available Items:</span> This list can be left blank if you will be adding this slot's available EquippedItems as a child of this Equipment Slot - they will be
			discovered automatically in-game. If the EquippedItems live somewhere else in your object's hierarchy, drag references to them to this list.
		</p>
        
		<p>
			<span class="font-weight-bold">VR Equip Trigger:</span> This trigger is the zone where a player would need to hold an equippable item in VR mode in order to equip it on this character.
			For example, on a Feral character's Chest item such as a saddle, this zone should probably be somewhere above the character's back.
		</p>
        
		<p>
			<span class="font-weight-bold">VR Unequip Trigger:</span> This trigger is the spot a player would need to grab in order to remove an equipped item in this slot in VR mode. This 
			trigger should probably be on or near the character's mesh somewhere, so the player feels like they are grabbing the equipment item itself when unequipping it.
		</p>
        
        
		<h4 class="mt-5">Setting Up Equipped Items</h4>
		<b-img src="/images/importer/module-equipment/equipped-item.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Item Type:</span> This is the type of the equipment item. Some item types may add a child "Pose Marker" object, if that item has activities that
			involve a partner being attached to this character, such as the Saddle or Belly Rig.
		</p>

		<p>
			<span class="font-weight-bold">Only For Body Types:</span> If your character has multiple body types (see the 
			<router-link :to="{name: 'tutorials', params: {tutorialName: 'ConfigurablePartsModule'}}">Configurable Parts</router-link> module) that are different enough in shape that one version of the
			item's mesh won't fit well on both, you will want to have different versions of the equipment item. Use this array to enter the name of the body type that this item should appear with, or
			leave it blank for equipment items that should always appear regardless of body type.
		</p>
        
		<b-img src="/images/importer/module-equipment/equipment-pose-marker.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Pose Marker:</span> After setting the Item Type, a PoseMarker prefab object may be automatically added as a child of the EquippedItem. This pose marker is used for
			informing the game where a partner should be positioned when interacting with this object. For example, see the Saddle setup above - the pose marker has a guide mesh indicating the orientation
			of the partner's hips, which should be placed so they are sitting on the saddle.
		</p>
        
		<b-img src="/images/importer/module-equipment/equipment-surface-guide.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Surface Guide Marker:</span> The Surface Guide marker is a child object of the Pose Marker, and indicates the position of the "sitting surface" for this
			equipment pose when in VR mode.
		</p>

		<p>
			There are two ways to set up an Equipped Item for an Equipment Slot - either the item is already present in the character model object and is deformed by its rig, or it was exported
			as a separate object but was weight-mapped to the same rig that the character uses in the modelling software.
		</p>

		<p>
			If the equipment item is already part of the character model object, all you need to do is add an EquippedItem component to the equipment item's mesh, and configure its fields and the child 
			Pose Marker object that is added based on Item Type, if applicable.
		</p>
        
		<b-img src="/images/importer/module-equipment/item-setup.jpg"></b-img>
		<p>
			If the equipment item was exported as a separate rigged object from the character model, it will need to be run through the "Skinned Mesh Renderer Setup" in the EquippedItem component. This is
			because even if an object has been weight-mapped to the same skeleton with all the same bone names, it won't be controlled by that separate rig in Unity. To get started with this
			workflow, first add an EquippedItem prefab (Prefabs -> EquipmentItem) as a child of the EquipmentSlot object. Then, add your equipment item anywhere in the scene by dragging its model file into 
			the Hierarchy. Make sure the equipment item is positioned correctly on your character, and with the EquippedItem object selected, drag the model into the "Item Model Object" field. Then, find
			any object in your character model with a skinned mesh renderer that will be used as a reference for which skeleton/rig to map the equipment item to, and drag it to the "Reference Skinned Mesh" 
			field. Clicking the "Attach Meshes to Character" button will then create copies of any skinned mesh renderers in your equipment object and add them as children of the EquippedItem object, and 
			destroy the original object. The equipment item's meshes should now be correctly attached to the character model's skeleton and should deform when its bones are moved.
		</p>
	</div>
</template>

<script>
export default {
		name: 'EquipmentModule',
}
</script>
